import React from 'react';

// L2 Infrastructure Profile — NON-SCORING (owner ruling 2026-09-26, SR-344..346). Replaces the pillar
// cards while the L2 framework is not ratified: verified architecture, dependencies and measured market /
// usage context, with no composite, pillar score or grade.

interface Source { url: string; kind: string; quote?: string; sha256?: string }
interface Row { label: string; value: string; state: string; note?: string; sources?: Source[] }
interface Group { id: string; title: string; rows: Row[] }
export interface L2ProfileData {
  framework_ref: string; framework_status: string; composite_state: string;
  network: string; token_role: string; is_single_network: boolean; reviewed_at?: string; registry_version?: string;
  groups: Group[]; policy: string[];
}

const tone = (s: string) => {
  switch (s) {
    case 'CURATED_VERIFIED': case 'MEASURED':
      return 'border-emerald-700/60 bg-emerald-900/20 text-emerald-300';
    case 'UNVERIFIED':
      return 'border-amber-700/60 bg-amber-900/20 text-amber-300';
    default:
      return 'border-slate-600/70 bg-slate-800/40 text-slate-300';
  }
};

const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };

const L2InfrastructureProfile: React.FC<{ profile: L2ProfileData; symbol: string }> = ({ profile, symbol }) => (
  <div className="border border-[color:rgb(var(--c-border-rgb)/0.95)] rounded-[2px] bg-cmd-panel">
    <div className="px-6 pt-5 pb-4 border-b border-[color:rgb(var(--c-border-rgb)/0.95)] flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.16em] text-cmd-textDim">L2 Infrastructure Profile · non-scoring</div>
        <div className="mt-1 text-[18px] font-semibold text-cmd-text">{symbol} · {profile.network}</div>
        {profile.token_role && <div className="mt-1 text-[12.5px] text-cmd-textMuted max-w-[80ch]">{profile.token_role}</div>}
        {!profile.is_single_network && profile.token_role && (
          <div className="mt-1 text-[12px] text-amber-300/90">This token does not correspond to a single L2 network — the profile describes what it is attached to.</div>
        )}
      </div>
      <div className="text-right">
        <div className="text-[13px] font-semibold tracking-wide text-cmd-text">QUALITY · NOT YET SCORED</div>
        <div className="mt-1 inline-block rounded border px-2 py-[2px] text-[10px] font-mono border-slate-600/70 bg-slate-800/40 text-slate-300">
          {profile.framework_ref} · {profile.framework_status.replace(/_/g, ' ')}
        </div>
        <div className="mt-1 text-[10.5px] font-mono text-cmd-textDim">composite {profile.composite_state.replace(/_/g, ' ')}</div>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[color:rgb(var(--c-border-rgb)/0.95)]">
      {profile.groups.map((g) => (
        <section key={g.id} className="bg-cmd-panel px-6 py-5">
          <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-cmd-textDim mb-3">{g.title}</h3>
          <div className="space-y-3">
            {g.rows.map((r, i) => (
              <div key={i}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[12.5px] text-cmd-textMuted">{r.label}</span>
                  <span className={`shrink-0 rounded border px-1.5 py-[1px] text-[9px] font-mono ${tone(r.state)}`}>{r.state.replace(/_/g, ' ')}</span>
                </div>
                <div className="mt-0.5 text-[13px] text-cmd-text leading-snug">{r.value}</div>
                {r.note && <div className="mt-0.5 text-[11px] text-cmd-textDim leading-snug">{r.note}</div>}
                {r.sources && r.sources.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10.5px] font-mono">
                    {r.sources.map((s, j) => (
                      <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" title={s.quote ? `"${s.quote}"` : undefined}
                        className="text-cmd-textDim hover:text-cmd-text underline-offset-2 hover:underline">
                        {s.kind === 'PRIMARY' ? '◆' : '◇'} {host(s.url)}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>

    <div className="px-6 py-4 border-t border-[color:rgb(var(--c-border-rgb)/0.95)] text-[11.5px] text-cmd-textMuted space-y-1">
      {profile.policy.map((p, i) => <div key={i}>{p}</div>)}
      <div className="text-[10.5px] font-mono text-cmd-textDim pt-1">
        ◆ primary source · ◇ secondary · architecture registry {profile.registry_version || '—'}{profile.reviewed_at ? ` · reviewed ${profile.reviewed_at}` : ''}
      </div>
    </div>
  </div>
);

export default L2InfrastructureProfile;
